/**
 * @module TerminBuchen
 * @description Team-Buchung: Mitarbeiter buchen einen Termin in den
 *   Sprechstunden-Slots eines Vorgesetzten/Kollegen.
 *
 *   Workflow:
 *     1. User-Auswahl (Dropdown mit allen Usern, die `is_bookable=true` Bloecke haben)
 *     2. Wochen-Ansicht der freien Slots (GET /api/calendar/bookable/:userId)
 *     3. Klick auf Slot → Buchungs-Modal (Betreff, Dauer, Beschreibung)
 *     4. POST /api/calendar/book/:userId → Outlook-Event + Notification
 *
 * @route /kalender/buchen
 * @since v2.317.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight, Check, X,
  AlertTriangle, Clock, Users, ArrowLeft,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';
const f = (url, opts = {}) => fetch(`${API}${url}`, { credentials: 'include', ...opts });

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 07:00 – 19:00
const HOUR_PX = 56;

// ─── Helpers ────────────────────────────────────────────────────────────────
function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = x.getDay();
  const diff = (dow + 6) % 7; // Mo=0
  x.setDate(x.getDate() - diff);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function isoDate(d) {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() - x.getTimezoneOffset());
  return x.toISOString().slice(0, 10);
}
function fmtTime(d) {
  return new Date(d).toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' });
}
function fmtDateRange(from, to) {
  const opts = { day: '2-digit', month: '2-digit' };
  return `${from.toLocaleDateString('de-AT', opts)} – ${to.toLocaleDateString('de-AT', opts)}`;
}

// ─── BookingModal ───────────────────────────────────────────────────────────
function BookingModal({ open, slot, targetUser, onClose, onBooked, t }) {
  const [form, setForm] = useState({ subject: '', duration: 30, description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setForm({ subject: '', duration: 30, description: '' });
  }, [open]);

  if (!open || !slot) return null;

  const slotStart = new Date(slot.start);
  const slotEnd = new Date(slot.end);
  const maxMinutes = Math.floor((slotEnd - slotStart) / 60000);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim()) {
      setError(t('terminBuchen.errorSubject', 'Betreff erforderlich'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const bookEnd = new Date(slotStart.getTime() + form.duration * 60000);
      if (bookEnd > slotEnd) {
        setError(t('terminBuchen.errorTooLong', 'Dauer ueberschreitet den Slot'));
        setSaving(false);
        return;
      }
      const r = await f(`/api/calendar/book/${targetUser.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: form.subject,
          start: slotStart.toISOString(),
          end: bookEnd.toISOString(),
          description: form.description,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `${r.status}`);
      }
      onBooked();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-md rounded-2xl border p-5" style={{ background: 'var(--bg-card, #fff)', borderColor: 'var(--border-card, #E5E7EB)' }}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
              {t('terminBuchen.modalTitle', 'Termin buchen')}
            </h2>
            <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('terminBuchen.modalSubtitle', 'bei')} <strong>{targetUser?.name}</strong>
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
            <X className="w-5 h-5" style={{ color: 'var(--text-tertiary, #6B7280)' }} />
          </button>
        </div>

        <div className="mb-4 p-3 rounded-lg border" style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-inset, #F8F9FC)' }}>
          <div className="text-xs font-semibold uppercase mb-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
            {t('terminBuchen.slot', 'Verfuegbarer Slot')}
          </div>
          <div className="text-sm" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
            {slotStart.toLocaleDateString('de-AT', { weekday: 'long', day: '2-digit', month: '2-digit' })}
            {' · '}{fmtTime(slotStart)} – {fmtTime(slotEnd)}
            {' · '}{maxMinutes} min
          </div>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('terminBuchen.subject', 'Betreff')} *
            </label>
            <input
              type="text"
              value={form.subject}
              onChange={(e) => setForm(s => ({ ...s, subject: e.target.value }))}
              placeholder={t('terminBuchen.subjectPlaceholder', 'z.B. Quartalsgespraech')}
              className="w-full px-3 py-2 rounded-lg border text-sm"
              style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('terminBuchen.duration', 'Dauer')}
            </label>
            <div className="flex items-center gap-2">
              {[15, 30, 45, 60].filter(d => d <= maxMinutes).map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setForm(s => ({ ...s, duration: d }))}
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                    form.duration === d ? 'bg-[#338edc] text-white border-[#338edc]' : ''
                  }`}
                  style={form.duration === d ? {} : {
                    borderColor: 'var(--border-default, #D1D5DB)',
                    background: 'var(--bg-input, #fff)',
                    color: 'var(--text-secondary, #4B5563)',
                  }}
                >
                  {d} min
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('terminBuchen.description', 'Beschreibung (optional)')}
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(s => ({ ...s, description: e.target.value }))}
              rows={3}
              placeholder={t('terminBuchen.descriptionPlaceholder', 'Worum geht es?')}
              className="w-full px-3 py-2 rounded-lg border text-sm resize-none"
              style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
            />
          </div>

          {error && (
            <div className="text-xs text-red-500 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-secondary, #4B5563)' }}>
              {t('terminBuchen.cancel', 'Abbrechen')}
            </button>
            <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#338edc] hover:bg-[#2876b8] disabled:opacity-50">
              {saving ? t('terminBuchen.saving', 'Buche...') : t('terminBuchen.book', 'Termin buchen')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────────────────────
export default function TerminBuchen() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const selectedUser = useMemo(
    () => users.find(u => u.id === Number(selectedUserId)),
    [users, selectedUserId]
  );

  const weekEnd = useMemo(() => addDays(weekStart, 5), [weekStart]); // Mo–Fr+1

  // Load bookable users
  useEffect(() => {
    f('/api/calendar/users')
      .then(r => r.json())
      .then(d => {
        setUsers(d.users || []);
        // Pre-select from URL
        const urlUserId = searchParams.get('user');
        if (urlUserId) setSelectedUserId(urlUserId);
        else if (d.users?.length === 1) setSelectedUserId(String(d.users[0].id));
      })
      .catch(err => setError(err.message));
  }, []);

  // Load slots when user/week changes
  useEffect(() => {
    if (!selectedUserId) {
      setSlots([]);
      return;
    }
    setLoading(true);
    setError(null);
    const start = new Date(weekStart);
    const end = addDays(weekStart, 5);
    f(`/api/calendar/bookable/${selectedUserId}?start=${start.toISOString()}&end=${end.toISOString()}`)
      .then(r => r.json())
      .then(d => setSlots(d.slots || []))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [selectedUserId, weekStart]);

  const handleSlotClick = (slot) => {
    if (!slot.available) return;
    setSelectedSlot(slot);
    setModalOpen(true);
  };

  const handleBooked = () => {
    setSuccess(t('terminBuchen.successMsg', 'Termin erfolgreich gebucht!'));
    setTimeout(() => setSuccess(null), 4000);
    // Reload slots
    if (selectedUserId) {
      const start = new Date(weekStart);
      const end = addDays(weekStart, 5);
      f(`/api/calendar/bookable/${selectedUserId}?start=${start.toISOString()}&end=${end.toISOString()}`)
        .then(r => r.json())
        .then(d => setSlots(d.slots || []));
    }
  };

  const days = useMemo(() => Array.from({ length: 5 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const dayLabels = [
    t('kalender.weekday.mo', 'Mo'),
    t('kalender.weekday.tu', 'Di'),
    t('kalender.weekday.we', 'Mi'),
    t('kalender.weekday.th', 'Do'),
    t('kalender.weekday.fr', 'Fr'),
  ];

  // Group slots by day
  const slotsByDay = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const d = new Date(s.start);
      const key = isoDate(d);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    return map;
  }, [slots]);

  return (
    <div className="px-6 py-5 min-h-screen" style={{ background: 'var(--bg-page, #F5F6FA)' }}>
      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/kalender')}
            className="p-2 rounded-lg border hover:bg-gray-50"
            style={{ borderColor: 'var(--border-card, #E5E7EB)', background: 'var(--bg-card, #fff)' }}
            title={t('terminBuchen.backToCalendar', 'Zurueck zum Kalender')}
          >
            <ArrowLeft className="w-4 h-4" style={{ color: 'var(--text-secondary, #4B5563)' }} />
          </button>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl" style={{ background: '#EFF6FF' }}>
              <CalendarIcon size={20} style={{ color: '#338edc' }} />
            </div>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                {t('terminBuchen.title', 'Termin buchen')}
              </h1>
              <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('terminBuchen.subtitle', 'Buche einen Termin in den Sprechstunden eines Kollegen')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Success Banner */}
      {success && (
        <div className="mb-4 p-3 rounded-lg border flex items-center gap-2" style={{ borderColor: '#10B981', background: '#ECFDF5' }}>
          <Check className="w-5 h-5" style={{ color: '#10B981' }} />
          <span className="text-sm font-medium" style={{ color: '#065F46' }}>{success}</span>
        </div>
      )}

      {/* User Picker Card */}
      <div className="mb-4 rounded-2xl border p-4" style={{ background: 'var(--bg-card, #fff)', borderColor: 'var(--border-card, #E5E7EB)' }}>
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5" style={{ color: 'var(--text-tertiary, #6B7280)' }} />
          <label className="text-sm font-semibold" style={{ color: 'var(--text-secondary, #4B5563)' }}>
            {t('terminBuchen.selectUser', 'Bei wem moechtest du buchen?')}
          </label>
          <select
            value={selectedUserId || ''}
            onChange={(e) => {
              setSelectedUserId(e.target.value);
              setSearchParams(e.target.value ? { user: e.target.value } : {});
            }}
            className="flex-1 max-w-md px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border-default, #D1D5DB)', background: 'var(--bg-input, #fff)', color: 'var(--text-primary, #1A1A2E)' }}
          >
            <option value="">{t('terminBuchen.selectPlaceholder', '— Auswaehlen —')}</option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
        </div>
        {users.length === 0 && (
          <div className="mt-3 text-xs flex items-center gap-1.5" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
            <AlertTriangle className="w-4 h-4" />
            {t('terminBuchen.noUsers', 'Es gibt aktuell keine User mit buchbaren Zeitbloecken.')}
          </div>
        )}
      </div>

      {/* Week View Card */}
      {selectedUserId && (
        <div className="rounded-2xl border" style={{ background: 'var(--bg-card, #fff)', borderColor: 'var(--border-card, #E5E7EB)' }}>
          {/* Toolbar */}
          <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWeekStart(addDays(weekStart, -7))}
                className="p-2 rounded-lg border hover:bg-gray-50"
                style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
              >
                <ChevronLeft className="w-4 h-4" style={{ color: 'var(--text-secondary, #4B5563)' }} />
              </button>
              <button
                onClick={() => setWeekStart(startOfWeek(new Date()))}
                className="px-3 py-2 rounded-lg border text-sm font-semibold hover:bg-gray-50"
                style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}
              >
                {t('terminBuchen.today', 'Heute')}
              </button>
              <button
                onClick={() => setWeekStart(addDays(weekStart, 7))}
                className="p-2 rounded-lg border hover:bg-gray-50"
                style={{ borderColor: 'var(--border-card, #E5E7EB)' }}
              >
                <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-secondary, #4B5563)' }} />
              </button>
              <span className="ml-3 text-sm font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                {fmtDateRange(weekStart, addDays(weekStart, 4))}
              </span>
            </div>
            {selectedUser && (
              <div className="text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                {t('terminBuchen.bookingFor', 'Buche bei:')} <strong style={{ color: 'var(--text-primary, #1A1A2E)' }}>{selectedUser.name}</strong>
              </div>
            )}
          </div>

          {/* Loading / Error */}
          {loading && (
            <div className="p-8 text-center text-sm" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
              {t('terminBuchen.loading', 'Lade Slots...')}
            </div>
          )}
          {error && (
            <div className="p-4 text-sm flex items-center gap-2" style={{ color: '#DC2626' }}>
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}

          {/* Day Grid */}
          {!loading && !error && (
            <div className="grid grid-cols-5 divide-x" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
              {days.map((day, i) => {
                const key = isoDate(day);
                const daySlots = slotsByDay.get(key) || [];
                const isToday = isoDate(day) === isoDate(new Date());
                return (
                  <div key={key} className="min-h-[300px]">
                    <div
                      className="px-3 py-2 border-b text-center"
                      style={{
                        borderColor: 'var(--border-card, #E5E7EB)',
                        background: isToday ? '#EFF6FF' : 'transparent',
                      }}
                    >
                      <div className="text-xs font-semibold uppercase" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                        {dayLabels[i]}
                      </div>
                      <div className="text-base font-bold" style={{ color: isToday ? '#338edc' : 'var(--text-primary, #1A1A2E)' }}>
                        {day.getDate()}.{day.getMonth() + 1}.
                      </div>
                    </div>
                    <div className="p-2 space-y-2">
                      {daySlots.length === 0 && (
                        <div className="text-xs text-center py-4" style={{ color: 'var(--text-muted, #9CA3AF)' }}>
                          —
                        </div>
                      )}
                      {daySlots.map((slot, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSlotClick(slot)}
                          disabled={!slot.available}
                          className={`w-full text-left p-2 rounded-lg border-l-4 text-xs transition-all ${
                            slot.available
                              ? 'hover:shadow-sm cursor-pointer hover:translate-x-0.5'
                              : 'cursor-not-allowed opacity-50'
                          }`}
                          style={{
                            borderLeftColor: slot.color || '#338edc',
                            background: slot.available ? 'var(--bg-inset, #F8F9FC)' : '#F3F4F6',
                          }}
                        >
                          <div className="font-semibold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                            {fmtTime(slot.start)} – {fmtTime(slot.end)}
                          </div>
                          <div className="text-[10px] mt-0.5" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
                            {slot.label}
                          </div>
                          {!slot.available && (
                            <div className="text-[10px] mt-0.5" style={{ color: '#DC2626' }}>
                              {t('terminBuchen.busy', 'Belegt')}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <BookingModal
        open={modalOpen}
        slot={selectedSlot}
        targetUser={selectedUser}
        onClose={() => { setModalOpen(false); setSelectedSlot(null); }}
        onBooked={handleBooked}
        t={t}
      />
    </div>
  );
}
